import { useCallback } from "react";
import { useTokenOps } from "../../components/TokenContext";
import { hostname } from "../../providers/api";

// Authenticated fetch helper for the Data Seamless screens.
//
// Mirrors the `api(method, path, body, isForm)` helper of the standalone test UI
// (backend/scripts/test_v4_expert.html) but drops its own login/token handling:
// the token comes from the admin's TokenContext (tokenOps.account) and the base
// URL from providers/api.js `hostname` (= REACT_APP_API_ENDPOINT/api), exactly
// like TraceabilitySection.js. Paths are passed WITHOUT the /api prefix, e.g.
// `/projects/:id/gaps`.
//
// Returns { ok, status, data } and never throws — network errors resolve to
// { ok: false, status: 0, data: { error } } so callers render a message instead.
export const useDataSeamlessApi = () => {
  const tokenOps = useTokenOps();
  const accessToken = tokenOps.account?.accessToken;
  const accountId = tokenOps.account?.data?.accountId;

  const api = useCallback(
    async (method, path, body = null, isForm = false) => {
      const headers = {};
      if (accessToken) headers.authorization = "Bearer " + accessToken;
      if (body && !isForm) headers["Content-Type"] = "application/json";

      const opts = { method, headers };
      if (body) opts.body = isForm ? body : JSON.stringify(body);

      try {
        const res = await fetch(`${hostname}${path}`, opts);
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          data = { raw: text };
        }
        return { ok: res.ok, status: res.status, data };
      } catch (e) {
        return { ok: false, status: 0, data: { error: e.message } };
      }
    },
    [accessToken]
  );

  return { api, accountId };
};
